import {computed, ref} from 'vue';
import {defineStore} from 'pinia';
import {showError} from '#imports';
import {readonlyState} from '@malt/utils';
import type {TaxRate, TaxRateWithAmount} from '@/openapi';
import {activityTrackerEndpoints} from '@/registry';

export const useEarningsOfTheMonthStore = defineStore('earnings', () => {
    const dailyRate = ref<number>();
    const amountExcludingServiceFeesIncludingTaxes = ref<number>();
    const freelanceFeesExcludingTaxes = ref<number>();
    const freelanceFeesIncludingTaxes = ref<number>();
    const freelanceFeesTaxes = ref<number>();
    const totalAmountExcludingTaxes = ref<number>();
    const totalAmountIncludingTaxes = ref<number>();
    const totalAmountTaxes = ref<number>();
    const freelancerFeesPercentage = ref<number>();
    const currency = ref<string>();
    const freelancerCurrency = ref<string>();
    const totalAmountInFreelancerCurrency = ref<number>();
    const taxes = ref<TaxRate[]>();
    const taxesWithAmount = ref<TaxRateWithAmount[]>();
    const freelancerFeeTaxRate = ref<number>();
    const estimationExchangeRate = ref<number | null>();

    async function fetchEarnings(activityReportId: string, totalWorkAmount: number) {
        try {
            const data = await activityTrackerEndpoints.evaluateEarningsForActivityReport({
                activityReportId,
                totalWorkAmount,
            });

            amountExcludingServiceFeesIncludingTaxes.value = data.amountExcludingServiceFeesIncludingTaxes;
            freelanceFeesExcludingTaxes.value = data.freelanceFeesExcludingTaxes;
            freelanceFeesIncludingTaxes.value = data.freelanceFeesIncludingTaxes;
            freelanceFeesTaxes.value = data.freelanceFeesTaxes;
            totalAmountExcludingTaxes.value = data.totalAmountExcludingTaxes;
            totalAmountIncludingTaxes.value = data.totalAmountIncludingTaxes;
            totalAmountTaxes.value = data.totalAmountTaxes;
            currency.value = data.currencyCode;
            freelancerFeesPercentage.value = data.freelancerFeesPercentage;
            dailyRate.value = data.freelancerDailyRate;
            freelancerCurrency.value = data.freelancerCurrencyCode;
            totalAmountInFreelancerCurrency.value = data.totalAmountInFreelancerCurrency;
            taxes.value = data.taxes;
            taxesWithAmount.value = data.taxesWithAmount;
            freelancerFeeTaxRate.value = data.freelancerFeeTaxRate;
            estimationExchangeRate.value = data.estimationExchangeRate;
        } catch (e: any) {
            showError({statusCode: 404, stack: e});
        }
    }

    const freelancerFeesPercentageValue = computed(() => freelancerFeesPercentage.value && freelancerFeesPercentage.value * 100);

    const taxesValues = computed(() =>
        taxes.value?.map(({name, rate, type}) => ({
            name,
            type,
            rate: rate && rate * 100,
        })),
    );

    const taxesWithAmountValues = computed(() =>
        taxesWithAmount.value?.map(({name, rate, type, amount}) => ({
            name,
            type,
            rate,
            percent: rate && rate * 100,
            amount,
        })),
    );

    const freelancerFeeTaxPercent = computed(() => (freelancerFeeTaxRate.value ?? 0) * 100);

    return {
        fetchEarnings,
        freelancerFeesPercentageValue,
        taxesValues,
        taxesWithAmountValues,
        freelancerFeeTaxPercent,
        dailyRate: readonlyState(dailyRate),
        amountExcludingServiceFeesIncludingTaxes: readonlyState(amountExcludingServiceFeesIncludingTaxes),
        freelanceFeesExcludingTaxes: readonlyState(freelanceFeesExcludingTaxes),
        freelanceFeesIncludingTaxes: readonlyState(freelanceFeesIncludingTaxes),
        freelanceFeesTaxes: readonlyState(freelanceFeesTaxes),
        totalAmountExcludingTaxes: readonlyState(totalAmountExcludingTaxes),
        totalAmountIncludingTaxes: readonlyState(totalAmountIncludingTaxes),
        totalAmountTaxes: readonlyState(totalAmountTaxes),
        currency: readonlyState(currency),
        freelancerCurrency: readonlyState(freelancerCurrency),
        totalAmountInFreelancerCurrency: readonlyState(totalAmountInFreelancerCurrency),
        estimationExchangeRate: readonlyState(estimationExchangeRate),
    };
});
